import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MapPin, ChevronRight, Loader2, Calendar, User, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  actief: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  afgerond: 'bg-slate-100 text-slate-600 border-slate-200',
  no_go: 'bg-red-100 text-red-800 border-red-200',
};

const FASE_WEKEN = [[1, 2], [3, 4], [5, 6], [7, 8], [9, 12]];

function getCurrentWeek(startDatum) {
  if (!startDatum) return 0;
  const start = new Date(startDatum);
  const now = new Date();
  const diff = Math.floor((now - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return Math.max(0, diff);
}

function getCurrentFaseIndex(startDatum) {
  const week = getCurrentWeek(startDatum);
  if (week <= 0) return -1;
  for (let i = 0; i < FASE_WEKEN.length; i++) {
    if (week >= FASE_WEKEN[i][0] && week <= FASE_WEKEN[i][1]) return i;
  }
  return FASE_WEKEN.length - 1;
}

export default function ImplementatiesOverzicht() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [locaties, setLocaties] = useState([]);
  const [newForm, setNewForm] = useState({ locatie_slug: '', type: 'nieuw', start_datum: '', op_manager: '', interim_bl: '', thuislocatie: '', tl_thuislocatie: '' });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/implementaties`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      toast.error('Fout bij laden implementaties');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNewModal = async () => {
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/implementaties/beschikbare-locaties`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setLocaties(await res.json());
    } catch { /* ignore */ }
    setShowNew(true);
  };

  const createImpl = async () => {
    if (!newForm.locatie_slug || !newForm.start_datum || !newForm.op_manager) {
      toast.error('Vul locatie, startdatum en OP manager in'); return;
    }
    setCreating(true);
    try {
      const token = getAccessToken();
      const loc = locaties.find(l => l.slug === newForm.locatie_slug);
      const startDate = new Date(newForm.start_datum);
      const goNoGo = new Date(startDate); goNoGo.setDate(goNoGo.getDate() + 8 * 7);
      const body = {
        ...newForm,
        locatie_naam: loc?.naam || newForm.locatie_slug,
        go_no_go_datum: goNoGo.toISOString().split('T')[0],
      };
      const res = await fetch(`${API}/implementaties`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Fout'); }
      toast.success('Implementatie aangemaakt');
      setShowNew(false);
      setNewForm({ locatie_slug: '', type: 'nieuw', start_datum: '', op_manager: '', interim_bl: '', thuislocatie: '', tl_thuislocatie: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setCreating(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="impl-loading">
        <Loader2 className="w-8 h-8 animate-spin text-tw-blue" />
        <span className="ml-3 text-slate-500">Implementaties laden...</span>
      </div>
    );
  }

  const actief = data.filter(d => d.status === 'actief');
  const overig = data.filter(d => d.status !== 'actief');

  return (
    <div className="space-y-6" data-testid="implementaties-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Implementaties</h1>
          <p className="text-sm text-slate-500 mt-0.5">{actief.length} actief van {data.length} totaal</p>
        </div>
        <Button onClick={openNewModal} className="bg-tw-blue text-white hover:bg-tw-blue-light" data-testid="new-impl-btn">
          <Plus className="w-4 h-4 mr-1.5" />Nieuwe implementatie
        </Button>
      </div>

      {actief.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {actief.map(impl => {
            const week = getCurrentWeek(impl.start_datum);
            const faseIdx = getCurrentFaseIndex(impl.start_datum);
            const fase = impl.fases?.[faseIdx];
            return (
              <Card
                key={impl.locatie_slug}
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/management/implementaties/${impl.locatie_slug}`)}
                data-testid={`impl-card-${impl.locatie_slug}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 group-hover:text-tw-blue transition-colors">{impl.locatie_naam}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${STATUS_STYLE[impl.status] || STATUS_STYLE.actief}`}>
                        {impl.status}
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-tw-blue transition-colors mt-1" />
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Week {Math.min(week, 12)} van 12</span>
                      <span>{fase?.label || '—'}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-tw-blue rounded-full transition-all duration-500" style={{ width: `${Math.min((week / 12) * 100, 100)}%` }} />
                    </div>
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <User className="w-3.5 h-3.5" />
                      <span>OP: <strong className="text-slate-700">{impl.op_manager || '—'}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <Users className="w-3.5 h-3.5" />
                      <span>BL: <strong className="text-slate-700">{impl.interim_bl || '—'}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 col-span-2">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Go/No-Go: <strong className="text-slate-700">{impl.go_no_go_datum || '—'}</strong></span>
                    </div>
                  </div>

                  {/* KPI badges */}
                  {impl.kpis?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                      {impl.kpis.map(kpi => (
                        <span key={kpi.naam} className={`text-xs px-2 py-0.5 rounded-full ${kpi.status === 'behaald' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {kpi.naam}: {kpi.actueel != null ? `${kpi.actueel}${kpi.eenheid === '%' ? '%' : ''}` : '—'} / {kpi.target}{kpi.eenheid === '%' ? '%' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {overig.length > 0 && (
        <div>
          <h2 className="text-base font-semibold text-slate-700 mb-3">Afgerond / No-Go</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {overig.map(impl => (
              <Card key={impl.locatie_slug} className="border-0 shadow-sm opacity-70 cursor-pointer hover:opacity-100 transition-opacity"
                onClick={() => navigate(`/management/implementaties/${impl.locatie_slug}`)}
                data-testid={`impl-card-${impl.locatie_slug}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-700">{impl.locatie_naam}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${STATUS_STYLE[impl.status] || STATUS_STYLE.actief}`}>
                        {impl.status}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Nieuwe implementatie modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="new-impl-modal">
          <DialogHeader><DialogTitle>Nieuwe implementatie</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Locatie *</label>
              <Select value={newForm.locatie_slug} onValueChange={v => setNewForm(p => ({ ...p, locatie_slug: v }))}>
                <SelectTrigger data-testid="new-impl-locatie"><SelectValue placeholder="Selecteer locatie" /></SelectTrigger>
                <SelectContent>
                  {locaties.map(l => <SelectItem key={l.slug} value={l.slug}>{l.naam}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Type *</label>
              <Select value={newForm.type} onValueChange={v => setNewForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="new-impl-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nieuw">Nieuw</SelectItem>
                  <SelectItem value="overname">Overname</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Startdatum *</label>
              <Input type="date" value={newForm.start_datum} onChange={e => setNewForm(p => ({ ...p, start_datum: e.target.value }))} data-testid="new-impl-start" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">OP Manager *</label>
              <Input value={newForm.op_manager} onChange={e => setNewForm(p => ({ ...p, op_manager: e.target.value }))} placeholder="Naam" data-testid="new-impl-op" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Interim BL</label>
                <Input value={newForm.interim_bl} onChange={e => setNewForm(p => ({ ...p, interim_bl: e.target.value }))} placeholder="Naam" data-testid="new-impl-bl" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Thuislocatie</label>
                <Input value={newForm.thuislocatie} onChange={e => setNewForm(p => ({ ...p, thuislocatie: e.target.value }))} placeholder="Locatie" data-testid="new-impl-thuis" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">TL Thuislocatie</label>
              <Input value={newForm.tl_thuislocatie} onChange={e => setNewForm(p => ({ ...p, tl_thuislocatie: e.target.value }))} placeholder="Naam" data-testid="new-impl-tl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Annuleren</Button>
            <Button onClick={createImpl} disabled={creating} className="bg-tw-blue text-white hover:bg-tw-blue-light" data-testid="new-impl-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
